import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIsTabletUp } from '@/shared/hooks/useMediaQuery';
import { ArrowLeftIcon } from '@/shared/ui/icons';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { useHeaderActions, usePageHeaderSlot } from '@/shared/ui/layout/PageHeaderSlot';
import { cn } from '@/shared/utils/cn';

type PageHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  showBack?: boolean;
  backTo?: string;
  right?: ReactNode;
  className?: string;
  sticky?: boolean;
};

export const PageHeader = ({
  title,
  subtitle,
  showBack = false,
  backTo,
  right,
  className,
  sticky = true,
}: PageHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTabletUp = useIsTabletUp();
  const slot = usePageHeaderSlot();
  const headerActions = useHeaderActions();

  const handleBack = (): void => {
    if (backTo) navigate(backTo);
    else navigate(-1);
  };

  const body = (
    <>
      {showBack && (
        <IconButton label={t('common:back')} variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeftIcon className="h-5 w-5" />
        </IconButton>
      )}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-semibold text-ink md:text-lg lg:text-xl">{title}</h1>
        {subtitle != null && subtitle !== '' &&
          (typeof subtitle === 'string' ? (
            <p className="truncate text-xs text-ink-muted md:text-sm lg:text-base">{subtitle}</p>
          ) : (
            <div className="mt-0.5">{subtitle}</div>
          ))}
      </div>
      {(right || headerActions) && (
        <div className="flex shrink-0 items-center gap-2">
          {right}
          {headerActions}
        </div>
      )}
    </>
  );

  // Tablet+: render into the shared top-bar segment (once the slot ref mounts).
  if (isTabletUp) {
    if (!slot) return null;
    return createPortal(
      <div className={cn('flex h-16 items-center gap-3 px-4 lg:px-6', className)}>{body}</div>,
      slot,
    );
  }

  // Mobile: inline sticky header with the same constant height as the desktop top bar.
  return (
    <header
      className={cn(
        'flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:px-6',
        sticky && 'sticky top-0 z-20',
        className,
      )}
      style={{
        height: 'calc(env(safe-area-inset-top) + 4rem)',
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      {body}
    </header>
  );
};
